import { useMemo, useState, useEffect } from 'react';
import {
  DataTable,
  type Column,
  type Pagination,
} from '@/components/common/table';
import type { ActionKey, ColumnInfo } from './columns';
import { CellDisplay, createColumn, makeColumnLookup } from './columns';
import type { User } from '@/shared/types/user';
import { Button } from '@/components/ui';
import { Icon } from '@iconify/react';

const DISPLAY_PAGE_SIZE = 20;
const ALLOW_ITEMS_PER_PAGE = [10, 20, 50, 100];

export function UsersTable({
  items,
  count,
  onAction,
  loading = false,
}: {
  items: User[];
  count: number;
  onAction: (item: User, action: ActionKey) => void;
  loading?: boolean;
}) {
  const cols: ColumnInfo[] = useMemo(
    () => [
      createColumn({ key: 'displayName', displayName: 'Name' }),
      createColumn({ key: 'email' }),
      createColumn({ key: 'access', displayName: 'Access', align: 'center' }),
      createColumn({ key: 'actions', displayName: 'Actions', align: 'center' }),
    ],
    [],
  );
  const columnLookup = useMemo(() => makeColumnLookup(cols), [cols]);

  const [page, setPage] = useState(1);
  const [displayPageSize, setDisplayPageSize] = useState(DISPLAY_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [items.length]);

  const columns = useMemo(
    (): Column<User>[] => [
      {
        key: 'displayName',
        label: 'Name',
        display: ({ item }) => (
          <CellDisplay
            item={item}
            col={columnLookup.displayName}
            onAction={onAction}
          />
        ),
      },
      {
        key: 'email',
        label: 'Email',
        display: ({ item }) => (
          <CellDisplay
            item={item}
            col={columnLookup.email}
            onAction={onAction}
          />
        ),
      },
      {
        key: 'access',
        label: 'Access',
        align: 'center',
        display: ({ item }) => (
          <CellDisplay
            item={item}
            col={columnLookup.access}
            onAction={onAction}
          />
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        align: 'center',
        display: ({ item }) => (
          <CellDisplay
            item={item}
            col={columnLookup.actions}
            onAction={onAction}
          />
        ),
      },
    ],
    [columnLookup, onAction],
  );

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      page,
      itemsPerPage: displayPageSize,
      allowItemsPerPage: ALLOW_ITEMS_PER_PAGE,
      onPageChange: (nextPage: number) => setPage(nextPage),
      onItemsPerPageChange: (value: number) => {
        setDisplayPageSize(value);
        setPage(1);
      },
    }),
    [page, displayPageSize],
  );

  return (
    <DataTable<User>
      aria-label='Users'
      columns={columns}
      items={items}
      count={count}
      pagination={pagination}
      paginationVariant='numbered'
      loading={loading}
      className='text-sm text-gray-800'
      empty={{
        icon: 'mdi:account-group',
        title: 'No users found',
        body: 'No users match your current filters. Try changing the status or search, or add a new user.',
        actions: (
          <Button
            color='primary'
            startContent={
              <Icon icon='mdi:account-plus' width={18} height={18} />
            }
          >
            Create User
          </Button>
        ),
      }}
    />
  );
}
